import React, { useEffect, useState } from 'react'
import { Button, Checkbox, ConfigProvider, DatePicker, Form, Input, message, Modal, Select } from 'antd'
import { ArrowLeftOutlined, DeleteTwoTone, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { BackButton, BasicSettingWrapper, CustomSettingWrapper, FormButton, HorizontalFormWrapper, LogicTitle, SettingWrapper, SubTitle } from '../invoice.styled'
import dayjs from 'dayjs'
import { invoiceLogicColOptions, audienceLogicColOptions, optionConf, LOGIC_COL } from './conf'
import { addInvoice, addInvoiceSegment, fetchInvoiceLimit, fetchInvoiceRuleOptions, modInvoice, modInvoiceSegment, previewInvoice, checkFileInvoice } from '../../../apis/invoice'
import useParams from '../../../hook/useParams'
import MainTable from '../../../components/Basic/MainTable'
import { useSelector } from 'react-redux'

const { ITEM_CATEGORY, ITEM_NAME, CHANNEL, COLUMN } = LOGIC_COL
function InvoiceForm (props) {
  const { goBackPath, addMode, editMode, audienceMode, viewOnly } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { data } = location?.state || {}
  const user = useSelector(state => state.user)
  const { nowGroup, nowBrand, groups } = user || {}
  const adminMode = Boolean(groups?.find(i => i.id === nowGroup)?.groupType)
  const params = useParams({}, false)
  const { handleFetchParams, fetchParams, setReady, ready } = params
  const [startDate, setStartDate] = useState()
  const [logicNum, setLogicNum] = useState([1])
  const [selectedLogic, setSelectedLogic] = useState({})
  const [previewCol, setPreviewCol] = useState()
  const logicColOptions = (audienceMode ? audienceLogicColOptions : invoiceLogicColOptions?.filter(i => adminMode || i !== COLUMN))?.reduce((all, i) => ({ ...all, [i]: optionConf(ln_t)?.[i] }), {})

  const createApi = audienceMode ? addInvoiceSegment : addInvoice
  const editApi = audienceMode ? modInvoiceSegment : modInvoice

  const goBack = () => navigate(goBackPath)
  const defPlaceholder = (name, type) => `${type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''} ${ln_t(`invoice.${name}`)}`
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`invoice.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })

  const neededFilterCol = [ITEM_CATEGORY, ITEM_NAME, CHANNEL]

  const validateDateInterval = (interval) => {
    if (!interval || !interval[0] || !interval[1]) return null

    const startDate = dayjs(interval[0])
    const endDate = dayjs(interval[1])
    const today = dayjs()

    // Rule 1: Block intervals > 180 days
    if (endDate.diff(startDate, 'days') > 180) {
      return {
        validateStatus: 'error',
        type: 'interval'
      }
    }

    // Rule 2: Block start dates > 367 days ago
    if (today.diff(startDate, 'days') > 367) {
      return {
        validateStatus: 'error',
        type: 'historical'
      }
    }

    return null // Valid
  }

  const formatReqBody = val => {
    const { name, interval, saveAsFile, ...other } = val
    const dateError = validateDateInterval(interval)
    if (dateError) {
      const msg = dateError.type === 'interval'
        ? ln_t('message.error.intervalExceeds180Days')
        : ln_t('message.error.dataOlderThanOneYear')
      return { error: true, msg }
    }
    const ruleObj = {}
    Object.entries(other)?.forEach(([_key, val]) => {
      const [key, field] = _key?.split('-')
      if (!ruleObj?.[key]) ruleObj[key] = {}
      // Handle new array format for item_name and store_name (AND/OR logic)
      if ((field === 'item_name' || field === 'store_name') && Array.isArray(val)) {
        // Each element can contain commas for AND logic: ["A,B", "C"] = (A AND B) OR (C)
        ruleObj[key][field] = val
      } else {
        // Keep existing format for other fields (channel, item_category use AND logic)
        ruleObj[key][field] = Array.isArray(val) ? val.join(',') : val
      }
    })
    const rules = Object.values(ruleObj)
    if (!rules?.some(i => neededFilterCol.includes(i.col))) return { error: true, msg: ln_t('message.error.invoiceRequiredFilter') }
    return {
      error: false,
      reqBody: {
        groupId: nowGroup,
        brandId: nowBrand,
        name,
        startDate: dayjs(interval[0]).format('YYYY-MM-DD'),
        endDate: dayjs(interval[1]).format('YYYY-MM-DD'),
        ...saveAsFile === undefined ? {} : { saveAsFile: saveAsFile ? 1 : 0 },
        ...data?.id ? { id: data.id } : {},
        rules
      }
    }
  }
  const onAdd = async reqBody => {
    const res = await createApi(reqBody)
    if (res?.status === 200) {
      message.success(ln_t('message.success.create'))
      goBack()
    }
  }
  const handleAdd = async reqBody => {
    if (adminMode || audienceMode) return await onAdd(reqBody)
    const checkLimit = await fetchInvoiceLimit(nowGroup)
    if (checkLimit?.status !== 200) return
    const quota = checkLimit?.data?.remainingQuantity || 0
    if (quota < 1) return Modal.error({ title: ln_t('message.info.invoiceCreationLimit') })
    Modal.confirm({
      title: `${ln_t('message.info.createInvoiceConfirmation')} (${ln_t('invoice.quotaThisMonth')}${quota}${ln_t('invoice.quotaThisMonth2')})`,
      onOk: async () => await onAdd(reqBody)
    })
  }
  const handleModify = async (id, reqBody) => {
    const res = await editApi(id, reqBody)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      goBack()
    }
  }
  const handleSave = async item => {
    const { error, msg, reqBody } = formatReqBody(item)
    if (error) return message.error(msg)
    if (reqBody.saveAsFile === 1 && adminMode && addMode) {
      const checkResult = await checkFileInvoice(reqBody)
      if (checkResult?.status === 202) {
        // Show confirmation modal for safe to save
        Modal.confirm({
          title: ln_t('invoice.confirmSaveForm'),
          content: ln_t('invoice.confirmSaveMessage'),
          okText: ln_t('util.confirm'),
          cancelText: ln_t('util.cancel'),
          onOk: async () => await handleAdd(reqBody)
        })
        return
      } else if (checkResult?.status === 422) {
        // Show over-limit modal
        Modal.confirm({
          title: ln_t('invoice.overLimitTitle'),
          content: ln_t('invoice.overLimitMessage'),
          okText: ln_t('invoice.createDashboardWithoutForm'),
          cancelText: ln_t('invoice.cancelAdjustAgain'),
          onOk: async () => await handleAdd({ ...reqBody, saveAsFile: 0 }),
          onCancel: () => {} // Just close modal, equivalent to "Cancel Adjust Again"
        })
        return
      } else if (!checkResult) {
        // Handle API error (no response)
        console.error('CheckFile error: No response')
        message.error(ln_t('message.error.checkFile'))
        return
      }
    }
    // Original save logic for non-saveAsFile cases or edit mode
    if (addMode) {
      await handleAdd(reqBody)
    } else {
      await handleModify(data?.id, reqBody)
    }
  }

  // ----- table setting -----
  const handlePreview = async () => {
    try {
      await form.validateFields()
    } catch { return }
    const { error, msg, reqBody } = formatReqBody(form.getFieldsValue())
    if (error) return message.error(msg)
    handleFetchParams('add', { reqBody })
    if (!ready) setReady(true)
  }
  const previewTableApi = async () => await previewInvoice(fetchParams?.reqBody).then(res => {
    const { columns, values } = res?.data?.data || {}
    setPreviewCol(columns?.map(i => ({ title: i?.header, dataIndex: i?.header })))
    const data = values?.map((i, idx) => ({ ...i, generatedUniqueId: idx + 1 }))
    return { ...res, data }
  })

  // ----- logic setting -----
  const resetForm = (fields = [], num) => form.setFieldsValue(fields.reduce((all, i) => ({ ...all, [`${num}-${i}`]: undefined }), {}))
  const handleAddLogic = key => setLogicNum([...logicNum, Math.max(...logicNum) + 1])
  const handleDelLogic = num => {
    setLogicNum(logicNum?.filter(i => i !== num))
    setSelectedLogic({ ...selectedLogic, [num]: undefined })
    resetForm(['col', 'operator', 'val'], num)
  }
  const handleLogicColChange = async (e, num, reset = true) => {
    if (reset) resetForm(['operator', 'val'], num)
    const selected = logicColOptions[e]
    let logicValueOpt = []
    if (selected?.useSelector) {
      const res = await fetchInvoiceRuleOptions(e)
      if (res?.status === 200) logicValueOpt = res?.data
    }
    setSelectedLogic(prev => ({ ...prev, [num]: { ...selected, logicValueOpt } }))
  }

  // -----  -----
  useEffect(() => {
    if ((!addMode && !editMode) || (editMode && !data)) return goBack()
    if (addMode && !data) return
    const { name, startDate, endDate, rules, saveAsFile } = data
    const ruleFields = {}
    const count = rules?.map((i, idx) => {
      const num = idx + 1
      Object.entries(i)?.forEach(([key, val]) => {
        if (key.includes('col')) handleLogicColChange(val, num, false)
        ruleFields[`${num}-${key}`] = val.includes(',') ? val?.split(',') : val
      })
      return num
    })
    setLogicNum(count)
    form.setFieldsValue({ name, interval: [dayjs(startDate), dayjs(endDate)], saveAsFile: Boolean(saveAsFile), ...ruleFields })
  }, [])

  const disabledDate = date => {
    // Disable future dates
    if (dayjs(date).isAfter(dayjs(), 'day')) return true

    // Disable dates older than 367 days (prevents Rule 3 violations)
    if (dayjs(date).isBefore(dayjs().subtract(367, 'days'), 'day')) return true

    // If start date is selected, limit end date range
    if (startDate) {
      const oneYearBefore = dayjs(date).isBefore(dayjs(startDate).subtract(1, 'year'))
      const oneYearAfter = dayjs(date).isAfter(dayjs(startDate).add(1, 'year'))
      return oneYearBefore || oneYearAfter
    }

    return false
  }
  return <>
    <BackButton>
      <ArrowLeftOutlined onClick={goBack} />
      {viewOnly ? '' : ln_t(`util.${addMode ? 'addNew' : 'edit'}`)} {audienceMode ? ln_t('invoice.audienceSetting') : ln_t('sidebar.invoice')}
    </BackButton>
    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)} disabled={viewOnly}>
        <SettingWrapper>
          <BasicSettingWrapper>
            <SubTitle>{ln_t('sidebar.invoice')}</SubTitle>
            <Form.Item {...defConfig('name', 'input')}>
              <Input placeholder={defPlaceholder('name', 'input')} />
            </Form.Item>
            <Form.Item
              {...defConfig('timeInterval', 'select')}
              name="interval"
              rules={[
                { required: true, message: defPlaceholder('timeInterval', 'select') },
                {
                  validator: (_, value) => {
                    if (!value || !value[0] || !value[1]) return Promise.resolve()

                    const startDate = dayjs(value[0])
                    const endDate = dayjs(value[1])
                    const today = dayjs()

                    // Rule 1: Block intervals > 180 days
                    if (endDate.diff(startDate, 'days') > 180) {
                      return Promise.reject(new Error(ln_t('message.error.intervalExceeds180Days')))
                    }

                    // Rule 2: Block start dates > 367 days ago
                    if (today.diff(startDate, 'days') > 367) {
                      return Promise.reject(new Error(ln_t('message.error.dataOlderThanOneYear')))
                    }

                    return Promise.resolve()
                  }
                }
              ]}
            >
              <DatePicker.RangePicker
                onCalendarChange={dates => setStartDate(dates?.[0] || null)}
                onChange={() => form.validateFields(['interval'])}
                defaultPickerValue={dayjs().subtract(1, 'M')}
                disabledDate={disabledDate}
                status={validateDateInterval(form.getFieldValue('interval')) ? 'error' : undefined}
              />
            </Form.Item>
          </BasicSettingWrapper>

          <CustomSettingWrapper>
            <SubTitle>{ln_t('segment.criteria')} {ln_t('util.setting')}</SubTitle>
            {/* ---------- Logic ---------- */}
            {logicNum?.map(num => <div key={num}>
              <LogicTitle>
                {ln_t('criteria.logic')} #{num}
                {logicNum?.length > 1 && <DeleteTwoTone style={{ cursor: 'pointer' }} twoToneColor="#707070" onClick={() => handleDelLogic(num)} />}
              </LogicTitle>
              <HorizontalFormWrapper>
                <Form.Item name={`${num}-col`} rules={[{ required: true, message: defPlaceholder('logics.indicator', 'select') }]}>
                  <Select
                    showSearch
                    placeholder={ln_t('invoice.logics.indicator')}
                    options={Object.values(logicColOptions)}
                    optionFilterProp="children"
                    onChange={e => handleLogicColChange(e, num)}
                  />
                </Form.Item>
                <Form.Item name={`${num}-operator`} rules={[{ required: true, message: defPlaceholder('logics.function', 'select') }]}>
                  <Select
                    showSearch
                    placeholder={ln_t('invoice.logics.function')}
                    options={selectedLogic[num]?.operator?.map(i => ({ value: i, label: ln_t(`invoice.logics.${i}`) }))}
                    optionFilterProp="children"
                    disabled={!selectedLogic[num] || viewOnly}
                  />
                </Form.Item>
                <Form.Item name={`${num}-val`} rules={[{ required: true, message: defPlaceholder('logics.value', 'select') }]}>
                  {selectedLogic[num]?.useSelector
                    ? <Select
                        showSearch
                        mode={selectedLogic[num]?.multipleSelection ? 'tags' : ''}
                        placeholder={selectedLogic[num]?.example || ln_t('invoice.logics.value')}
                        options={selectedLogic[num]?.logicValueOpt || []}
                        optionFilterProp="children"
                        disabled={!selectedLogic[num] || viewOnly}
                      />
                    : selectedLogic[num]?.multipleSelection
                      ? <Select
                          placeholder={selectedLogic[num]?.example || ln_t('invoice.logics.value')}
                          dropdownRender={menu => <>
                            <span style={{ display: 'none' }}>{menu}</span>
                            <div style={{ color: 'rgba(0,0,0,.4)', padding: '2px 8px' }}>{ln_t('dashboard.inputMultipleInfo')}</div>
                          </>}
                          mode="tags"
                          disabled={!selectedLogic[num] || viewOnly}
                        />
                      : <Input
                          placeholder={selectedLogic[num]?.example || ln_t('invoice.logics.value')}
                          disabled={!selectedLogic[num] || viewOnly}
                        />}
                </Form.Item>
              </HorizontalFormWrapper>
            </div>)}

            {!viewOnly && <ConfigProvider theme={{ token: { colorPrimary: 'rgba(75,75,75,1)', colorPrimaryHover: 'rgba(75,75,75,.8)' } }}>
              <Button type="primary" style={{ width: '100%', marginTop: '16px' }} icon={<PlusOutlined />} onClick={handleAddLogic}>
                {ln_t('util.add')} {ln_t('criteria.criteriaLogic')}
              </Button>
            </ConfigProvider>}
            {!viewOnly && <div style={{ marginTop: '8px', color: '#999', fontSize: '14px', textAlign: 'left' }}>
              {ln_t('invoice.logicExplanation')}
            </div>}
            {!audienceMode && !viewOnly && <Button style={{ margin: '32px auto', display: 'flex' }} onClick={handlePreview}>{ln_t('util.preview')}</Button>}

            {ready &&
              <MainTable
                columns={previewCol}
                rowKey="generatedUniqueId"
                apiFunc={previewTableApi}
                params={params}
                disabledRow={record => !record.previewCount || !record.status}
                noPage
              />}
            {adminMode && (ready || viewOnly)
              ? <Form.Item name="saveAsFile" noStyle valuePropName="checked">
                  <Checkbox style={{ marginTop: '16px' }}>{ln_t('invoice.saveTable')}</Checkbox>
                </Form.Item>
              : ''}
            {!viewOnly && (ready || audienceMode)
              ? <FormButton>
                  <Button type="default" onClick={goBack}>{ln_t('util.cancel')}</Button>
                  <Button type="primary" htmlType="submit">{ln_t('util.confirm')}</Button>
                </FormButton>
              : ''}
          </CustomSettingWrapper>
        </SettingWrapper>
      </Form>

    </ConfigProvider>
  </>
}

export default InvoiceForm
